import { useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { InterstitialOverlay } from "@/components/overlay";
import { useAppStore } from "@/store";
import { windowModule } from "@/modules/window";
import { toastModule } from "@/modules/toast";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { clientEnvModule } from "@/modules/client-env";
import { localStorageModule } from "@/modules/local-storage";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent, CalendarConnectErrorType } from "@/domains/metrics/tracked-event";
import { getCalendarConnectSource } from "@/domains/calendar/getCalendarConnectSource";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialog,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { useState } from "react";
import { CalendarConnectionConfigurationListView } from "@/components/calendar/calendar-connection-configuration-list";

const trackConnectFailed = (errorType: CalendarConnectErrorType, errorMessage?: string) => {
  trackEvent(TrackedEvent.CalendarConnectFailed, {
    provider: "google",
    error_type: errorType,
    error_message: errorMessage,
    source: getCalendarConnectSource(),
  });
};

export const IntegrationsCalendarGoogleOAuthCallbackPage = observer(() => {
  const { store } = useAppStore();
  const location = useLocation();
  const googleCalendarStore = store.calendar.google;

  const systemPermissionsUi = store.systemPermissions.ui;

  const [isLoading, setIsLoading] = useState(true);
  const [connectedSubjectId, setConnectedSubjectId] = useState<string | null>(null);
  const account = connectedSubjectId ? googleCalendarStore.accounts.get(connectedSubjectId) : null;

  const [isIosCalendarSetup, setIsIosCalendarSetup] = useState(false);

  useEffectOnMount(() => {
    // On desktop, the settings modal may still be open from before the OAuth redirect.
    // Close it so the calendar-connect dialog is visible on top.
    if (store.settingsModal.isOpen) {
      store.settingsModal.close();
    }

    const params = new URLSearchParams(location.search);
    const error = params.get("error");
    const code = params.get("code");
    const state = params.get("state");

    const isDesktop = clientEnvModule.isDesktop();
    const storedState = localStorageModule.readGoogleCalendarOAuthState();
    setIsIosCalendarSetup(localStorageModule.readIosCalendarSetup());
    const isOnboardingOAuth = store.calendarOnboarding.isOAuthInProgress;

    // If we're in the web version and the OAuth state is not in localStorage,
    // it means the OAuth was initiated from Electron. Redirect back to Electron
    // so it can complete the connection using its stored state.
    if (!isDesktop && !storedState && state) {
      logger.info({
        message:
          "[IntegrationsCalendarGoogleOAuthCallbackPage] OAuth state not found in web localStorage, redirecting to Electron",
        info: { search: location.search },
      });

      const asyncLogic = async () => {
        try {
          const targetUrl = new URL(
            `mem://integrations/calendar/google/callback${location.search}`
          );
          await windowModule.navigateToExternal({ url: targetUrl });
        } catch (unknownError) {
          logger.error({
            message: "[IntegrationsCalendarGoogleOAuthCallbackPage] Failed to redirect to Electron",
            info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
          });
          toastModule.triggerToast({
            content: "Unable to complete Google Calendar connection. Please try again.",
          });
        } finally {
          // Always navigate away from the callback page
          store.navigation.goToNotes({ openInSameTab: true });
          store.settingsModal.open(SettingsTabId.CalendarAndMeetingsPage);
        }
      };

      void asyncLogic();
      return;
    }

    if (error) {
      trackConnectFailed("oauth_denied", error);
      toastModule.triggerToast({
        content: "Google Calendar connection failed. Please try again.",
      });
      if (isOnboardingOAuth) {
        store.calendarOnboarding.reset();
        store.navigation.goToNotes({ openInSameTab: true });
      } else {
        store.navigation.goToNotes({ openInSameTab: true });
        store.settingsModal.open(SettingsTabId.CalendarAndMeetingsPage);
      }
      return;
    }

    if (!code) {
      trackConnectFailed("missing_code");
      toastModule.triggerToast({
        content: "Missing authorization code from Google. Please try connecting again.",
      });
      if (isOnboardingOAuth) {
        store.calendarOnboarding.reset();
        store.navigation.goToNotes({ openInSameTab: true });
      } else {
        store.navigation.goToNotes({ openInSameTab: true });
        store.settingsModal.open(SettingsTabId.CalendarAndMeetingsPage);
      }
      return;
    }

    // If this was initiated from onboarding, transition to connection-pending
    // and navigate home. The calendar-select step will call completeConnection.
    if (isOnboardingOAuth) {
      store.calendarOnboarding.setPendingConnection({
        provider: "google",
        code,
        state,
      });
      store.navigation.goToNotes({ openInSameTab: true });
      return;
    }

    const redirectUri = windowModule.getGoogleCalendarOAuthRedirectUrl();
    const asyncLogic = async () => {
      try {
        const result = await store.calendar.google.completeConnection({ code, state, redirectUri });
        setConnectedSubjectId(result.subjectId);
        setIsLoading(false);

        trackEvent(TrackedEvent.CalendarConnectComplete, {
          provider: "google",
          calendar_connection_id: result.subjectId,
          source: getCalendarConnectSource(),
        });
      } catch (_unknownError) {
        const error = _unknownError as Error;
        const errorType: CalendarConnectErrorType = error.message?.includes("state mismatch")
          ? "state_mismatch"
          : "api_error";
        trackConnectFailed(errorType, error.message);

        logger.error({
          message:
            "[IntegrationsCalendarGoogleOAuthCallbackPage] Failed to complete Google Calendar connection",
          info: { error: objectModule.safeErrorAsJson(error) },
        });
        toastModule.triggerToast({
          content: "Unable to complete Google Calendar connection. Please try again.",
        });
        store.navigation.goToNotes({ openInSameTab: true });
        store.settingsModal.open(SettingsTabId.CalendarAndMeetingsPage);
      }
    };

    void asyncLogic();
  });

  return (
    <InterstitialOverlay>
      <MDSDialog isOpen isDismissable={false}>
        <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
          Choose calendars to see in Mem
        </MDSText>
        <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphSm}>
            Select calendars to view in Mem and use for meeting notifications
          </MDSText>
          <CalendarConnectionConfigurationListView
            email={account?.googleAccountEmail ?? null}
            provider="google"
            calendars={(account?.calendars ?? []).map(calendar => ({
              id: calendar.id,
              label: calendar.summary ?? calendar.id,
              isPrimary: calendar.isPrimary,
              isCalendarEnabled: calendar.isEnabled,
              isDisabled: googleCalendarStore.isUpdatingCalendars,
              webhookStatus: null,
              syncStartedAt: null,
              syncEndedAt: null,
              lastSyncEndedWithError: null,
              onToggle: (isEnabled: boolean) => {
                if (connectedSubjectId) {
                  void googleCalendarStore.toggleCalendar({
                    subjectId: connectedSubjectId,
                    calendarId: calendar.id,
                    isEnabled,
                  });
                }
              },
            }))}
            connectionStatus={isLoading ? "LOADING" : "CONNECTED"}
          />
        </MDSDialogTextContent>
        <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            isDisabled={isLoading}
            onClick={() => {
              if (isIosCalendarSetup) {
                windowModule.navigateToExternal({ url: new URL(`mem://ios-calendar-setup-done`) });
                localStorageModule.clearIosCalendarSetup();
                return;
              }

              store.navigation.goToNotes({ openInSameTab: true });

              if (clientEnvModule.isDesktop()) {
                systemPermissionsUi.openMeetingPermissionsModal();
                store.settingsModal.queueOpenAfterPermissions(
                  SettingsTabId.CalendarAndMeetingsPage
                );
                return;
              }

              store.settingsModal.open(SettingsTabId.CalendarAndMeetingsPage);
            }}
          >
            {isIosCalendarSetup || !clientEnvModule.isDesktop() ? "Finish" : "Next"}
          </MDSButton>
        </MDSButtonGroup>
      </MDSDialog>
    </InterstitialOverlay>
  );
});
